import { CommonModule, DatePipe, Location } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import BusinessRestServices from '../../services/business-rest.service';
import RestService from '../../services/core-rest.service';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import Session from '../../utils/session.service';
import { TranslatePipe } from '../../filters/translate.pipe';
import AngularFilterHelperService from '../../services/angular-filter-helper.service';
import { MarkdownBoldPipe } from '../../filters/markdown-bold.pipe';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';

@Component({
	standalone: true,
	selector: 'compliance-assessment-results',
	templateUrl: './compliance-assessment-results.component.html',
	imports: [CommonModule, FormsModule, DatePipe, RouterLink, TranslatePipe, MarkdownBoldPipe, LoaderOverlayComponent],
})
export default class ComplianceAssessmentResultsComponent implements OnInit {
	private readonly defaultActionDueDays = 90;
	private readonly employeePageSize = 200;
	private comparisonRequest = 0;
	private comparisonListRequest = 0;
	private comparisonCache = new Map<string, Promise<any[]>>();
	private processHistory: Array<{
		actionPlanOnly: boolean;
		planStep: number;
		reviewingCompletedPlan: boolean;
		resultsViewMode: string;
		planCompleted: boolean;
	}> = [];
	private readonly aiReportBlockingLoaderMs = 12000;
	private readonly aiReportMaxWaitMs = 90000;
	private route: ActivatedRoute = inject(ActivatedRoute);
	private restService: RestService = inject(RestService);
	private businessRestServices: BusinessRestServices = inject(BusinessRestServices);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private angularFilterHelperService: AngularFilterHelperService = inject(AngularFilterHelperService);
	private session: Session = inject(Session);
	private location: Location = inject(Location);

	vm: any = undefined;

	dimensions = [
		{ id: 'demands', nameKey: 'Compliance_Assessment.Dimension_Demands', source: 'COPSOQ III', benchmark: 55, referenceSd: 20, descKey: 'Compliance_Assessment.Dimension_Demands_Desc' },
		{ id: 'emo', nameKey: 'Compliance_Assessment.Dimension_Emotional', source: 'COPSOQ III', benchmark: 55, referenceSd: 22, descKey: 'Compliance_Assessment.Dimension_Emotional_Desc' },
		{ id: 'infl', nameKey: 'Compliance_Assessment.Dimension_Influence', source: 'COPSOQ III', benchmark: 50, referenceSd: 22, descKey: 'Compliance_Assessment.Dimension_Influence_Desc' },
		{ id: 'dev', nameKey: 'Compliance_Assessment.Dimension_Development', source: 'COPSOQ III', benchmark: 66, referenceSd: 20, descKey: 'Compliance_Assessment.Dimension_Development_Desc' },
		{ id: 'mean', nameKey: 'Compliance_Assessment.Dimension_Meaning', source: 'COPSOQ III', benchmark: 74, referenceSd: 17, descKey: 'Compliance_Assessment.Dimension_Meaning_Desc' },
		{ id: 'recog', nameKey: 'Compliance_Assessment.Dimension_Recognition', source: 'COPSOQ III', benchmark: 62, referenceSd: 21, descKey: 'Compliance_Assessment.Dimension_Recognition_Desc' },
		{ id: 'role', nameKey: 'Compliance_Assessment.Dimension_Role', source: 'COPSOQ III', benchmark: 72, referenceSd: 17, descKey: 'Compliance_Assessment.Dimension_Role_Desc' },
		{ id: 'lead', nameKey: 'Compliance_Assessment.Dimension_Leadership', source: 'COPSOQ III', benchmark: 58, referenceSd: 23, descKey: 'Compliance_Assessment.Dimension_Leadership_Desc' },
		{ id: 'support', nameKey: 'Compliance_Assessment.Dimension_Support', source: 'COPSOQ III', benchmark: 68, referenceSd: 19, descKey: 'Compliance_Assessment.Dimension_Support_Desc' },
		{ id: 'security', nameKey: 'Compliance_Assessment.Dimension_Security', source: 'COPSOQ III', benchmark: 72, referenceSd: 21, descKey: 'Compliance_Assessment.Dimension_Security_Desc' },
		{ id: 'worklife', nameKey: 'Compliance_Assessment.Dimension_Worklife', source: 'COPSOQ III', benchmark: 60, referenceSd: 23, descKey: 'Compliance_Assessment.Dimension_Worklife_Desc' },
		{ id: 'connect', nameKey: 'Compliance_Assessment.Dimension_Connectivity', source: 'Siffi Digital Supplement', benchmark: 60, referenceSd: 22, descKey: 'Compliance_Assessment.Dimension_Connectivity_Desc' },
		{ id: 'algo', nameKey: 'Compliance_Assessment.Dimension_Algorithmic', source: 'Siffi Digital Supplement', benchmark: 60, referenceSd: 22, descKey: 'Compliance_Assessment.Dimension_Algorithmic_Desc' },
		{ id: 'offensive', nameKey: 'Compliance_Assessment.Dimension_Offensive', source: 'COPSOQ III', benchmark: 10, exposure: true, descKey: 'Compliance_Assessment.Dimension_Offensive_Desc' }
	];

	itemDimensions: any[] = [
		{ dim: 'demands', textKey: 'Compliance_Assessment.Item_Demands_High_Pace' },
		{ dim: 'demands', textKey: 'Compliance_Assessment.Item_Demands_Uneven_Workload' },
		{ dim: 'demands', textKey: 'Compliance_Assessment.Item_Demands_Get_Behind' },
		{ dim: 'demands', textKey: 'Compliance_Assessment.Item_Demands_Core_Tasks_Time' },
		{ dim: 'demands', textKey: 'Compliance_Assessment.Item_Demands_Extra_Hours' },
		{ dim: 'emo', textKey: 'Compliance_Assessment.Item_Emotional_Demanding' },
		{ dim: 'emo', textKey: 'Compliance_Assessment.Item_Emotional_Disturbing' },
		{ dim: 'emo', textKey: 'Compliance_Assessment.Item_Emotional_Deal_With_People' },
		{ dim: 'infl', textKey: 'Compliance_Assessment.Item_Influence_Decisions' },
		{ dim: 'infl', textKey: 'Compliance_Assessment.Item_Influence_Work_Assigned' },
		{ dim: 'dev', textKey: 'Compliance_Assessment.Item_Development_Learn_New' },
		{ dim: 'dev', textKey: 'Compliance_Assessment.Item_Development_Use_Skills' },
		{ dim: 'mean', textKey: 'Compliance_Assessment.Item_Meaning_Meaningful' },
		{ dim: 'mean', textKey: 'Compliance_Assessment.Item_Meaning_Important' },
		{ dim: 'recog', textKey: 'Compliance_Assessment.Item_Recognition_Management' },
		{ dim: 'recog', textKey: 'Compliance_Assessment.Item_Recognition_Fairly_Treated' },
		{ dim: 'role', textKey: 'Compliance_Assessment.Item_Role_Expected' },
		{ dim: 'role', textKey: 'Compliance_Assessment.Item_Role_Objectives_Clear' },
		{ dim: 'lead', textKey: 'Compliance_Assessment.Item_Leadership_Job_Satisfaction' },
		{ dim: 'lead', textKey: 'Compliance_Assessment.Item_Leadership_Planning' },
		{ dim: 'support', textKey: 'Compliance_Assessment.Item_Support_Colleagues' },
		{ dim: 'support', textKey: 'Compliance_Assessment.Item_Support_Manager' },
		{ dim: 'security', textKey: 'Compliance_Assessment.Item_Security_Unemployed' },
		{ dim: 'security', textKey: 'Compliance_Assessment.Item_Security_Conflicts_Fair' },
		{ dim: 'security', textKey: 'Compliance_Assessment.Item_Security_Work_Distributed_Fairly' },
		{ dim: 'worklife', textKey: 'Compliance_Assessment.Item_Worklife_Demands_Interfere' },
		{ dim: 'worklife', textKey: 'Compliance_Assessment.Item_Worklife_Energy' },
		{ dim: 'worklife', textKey: 'Compliance_Assessment.Item_Worklife_Time' },
		{ dim: 'offensive', textKey: 'Compliance_Assessment.Item_Offensive_Bullying' },
		{ dim: 'offensive', textKey: 'Compliance_Assessment.Item_Offensive_Sexual_Attention' },
		{ dim: 'offensive', textKey: 'Compliance_Assessment.Item_Offensive_Threats' },
		{ dim: 'offensive', textKey: 'Compliance_Assessment.Item_Offensive_Physical_Violence' },
		{ dim: 'connect', textKey: 'Compliance_Assessment.Item_Connectivity_Check_Messages' },
		{ dim: 'connect', textKey: 'Compliance_Assessment.Item_Connectivity_Reachable' },
		{ dim: 'connect', textKey: 'Compliance_Assessment.Item_Connectivity_Switch_Off' },
		{ dim: 'connect', textKey: 'Compliance_Assessment.Item_Connectivity_Notifications' },
		{ dim: 'connect', textKey: 'Compliance_Assessment.Item_Connectivity_Respond_Out_Of_Hours' },
		{ dim: 'algo', textKey: 'Compliance_Assessment.Item_Algorithmic_Monitored' },
		{ dim: 'algo', textKey: 'Compliance_Assessment.Item_Algorithmic_Decisions' },
		{ dim: 'algo', textKey: 'Compliance_Assessment.Item_Algorithmic_Data_Use' },
		{ dim: 'algo', textKey: 'Compliance_Assessment.Item_Algorithmic_Appeal' },
		{ dim: 'algo', textKey: 'Compliance_Assessment.Item_Algorithmic_Stress' }
	];

	controlTemplates: any[] = [
		{ dimensionId: 'demands', riskAreaKey: 'Compliance_Assessment.Dimension_Demands', titleKey: 'Compliance_Assessment.Control_Workload_Review_Title', descriptionKey: 'Compliance_Assessment.Control_Workload_Review_Description' },
		{ dimensionId: 'demands', riskAreaKey: 'Compliance_Assessment.Dimension_Demands', titleKey: 'Compliance_Assessment.Control_Meeting_Free_Focus_Title', descriptionKey: 'Compliance_Assessment.Control_Meeting_Free_Focus_Description' },
		{ dimensionId: 'emo', riskAreaKey: 'Compliance_Assessment.Dimension_Emotional', titleKey: 'Compliance_Assessment.Control_Peer_Support_Debrief_Title', descriptionKey: 'Compliance_Assessment.Control_Peer_Support_Debrief_Description' },
		{ dimensionId: 'emo', riskAreaKey: 'Compliance_Assessment.Dimension_Emotional', titleKey: 'Compliance_Assessment.Control_Rotate_High_Intensity_Title', descriptionKey: 'Compliance_Assessment.Control_Rotate_High_Intensity_Description' },
		{ dimensionId: 'infl', riskAreaKey: 'Compliance_Assessment.Dimension_Influence', titleKey: 'Compliance_Assessment.Control_Involve_Teams_Planning_Title', descriptionKey: 'Compliance_Assessment.Control_Involve_Teams_Planning_Description' },
		{ dimensionId: 'infl', riskAreaKey: 'Compliance_Assessment.Dimension_Influence', titleKey: 'Compliance_Assessment.Control_Act_On_Feedback_Title', descriptionKey: 'Compliance_Assessment.Control_Act_On_Feedback_Description' },
		{ dimensionId: 'dev', riskAreaKey: 'Compliance_Assessment.Dimension_Development', titleKey: 'Compliance_Assessment.Control_Individual_Development_Plans_Title', descriptionKey: 'Compliance_Assessment.Control_Individual_Development_Plans_Description' },
		{ dimensionId: 'dev', riskAreaKey: 'Compliance_Assessment.Dimension_Development', titleKey: 'Compliance_Assessment.Control_Protected_Learning_Time_Title', descriptionKey: 'Compliance_Assessment.Control_Protected_Learning_Time_Description' },
		{ dimensionId: 'mean', riskAreaKey: 'Compliance_Assessment.Dimension_Meaning', titleKey: 'Compliance_Assessment.Control_Connect_Work_Impact_Title', descriptionKey: 'Compliance_Assessment.Control_Connect_Work_Impact_Description' },
		{ dimensionId: 'mean', riskAreaKey: 'Compliance_Assessment.Dimension_Meaning', titleKey: 'Compliance_Assessment.Control_Reduce_Low_Value_Work_Title', descriptionKey: 'Compliance_Assessment.Control_Reduce_Low_Value_Work_Description' },
		{ dimensionId: 'recog', riskAreaKey: 'Compliance_Assessment.Dimension_Recognition', titleKey: 'Compliance_Assessment.Control_Transparent_Reward_Review_Title', descriptionKey: 'Compliance_Assessment.Control_Transparent_Reward_Review_Description' },
		{ dimensionId: 'recog', riskAreaKey: 'Compliance_Assessment.Dimension_Recognition', titleKey: 'Compliance_Assessment.Control_Manager_Recognition_Habit_Title', descriptionKey: 'Compliance_Assessment.Control_Manager_Recognition_Habit_Description' },
		{ dimensionId: 'role', riskAreaKey: 'Compliance_Assessment.Dimension_Role', titleKey: 'Compliance_Assessment.Control_Clarify_Roles_Title', descriptionKey: 'Compliance_Assessment.Control_Clarify_Roles_Description' },
		{ dimensionId: 'role', riskAreaKey: 'Compliance_Assessment.Dimension_Role', titleKey: 'Compliance_Assessment.Control_Quarterly_Goals_Title', descriptionKey: 'Compliance_Assessment.Control_Quarterly_Goals_Description' },
		{ dimensionId: 'lead', riskAreaKey: 'Compliance_Assessment.Dimension_Leadership', titleKey: 'Compliance_Assessment.Control_Manager_One_To_One_Title', descriptionKey: 'Compliance_Assessment.Control_Manager_One_To_One_Description' },
		{ dimensionId: 'lead', riskAreaKey: 'Compliance_Assessment.Dimension_Leadership', titleKey: 'Compliance_Assessment.Control_Leadership_Feedback_Title', descriptionKey: 'Compliance_Assessment.Control_Leadership_Feedback_Description' },
		{ dimensionId: 'support', riskAreaKey: 'Compliance_Assessment.Dimension_Support', titleKey: 'Compliance_Assessment.Control_Team_Connection_Routines_Title', descriptionKey: 'Compliance_Assessment.Control_Team_Connection_Routines_Description' },
		{ dimensionId: 'support', riskAreaKey: 'Compliance_Assessment.Dimension_Support', titleKey: 'Compliance_Assessment.Control_Buddy_Mentoring_Title', descriptionKey: 'Compliance_Assessment.Control_Buddy_Mentoring_Description' },
		{ dimensionId: 'security', riskAreaKey: 'Compliance_Assessment.Dimension_Security', titleKey: 'Compliance_Assessment.Control_Change_Communication_Title', descriptionKey: 'Compliance_Assessment.Control_Change_Communication_Description' },
		{ dimensionId: 'security', riskAreaKey: 'Compliance_Assessment.Dimension_Security', titleKey: 'Compliance_Assessment.Control_Fair_Procedures_Title', descriptionKey: 'Compliance_Assessment.Control_Fair_Procedures_Description' },
		{ dimensionId: 'worklife', riskAreaKey: 'Compliance_Assessment.Dimension_Worklife', titleKey: 'Compliance_Assessment.Control_Working_Time_Norms_Title', descriptionKey: 'Compliance_Assessment.Control_Working_Time_Norms_Description' },
		{ dimensionId: 'worklife', riskAreaKey: 'Compliance_Assessment.Dimension_Worklife', titleKey: 'Compliance_Assessment.Control_Leave_Recovery_Title', descriptionKey: 'Compliance_Assessment.Control_Leave_Recovery_Description' },
		{ dimensionId: 'connect', riskAreaKey: 'Compliance_Assessment.Dimension_Connectivity', titleKey: 'Compliance_Assessment.Control_Right_To_Disconnect_Title', descriptionKey: 'Compliance_Assessment.Control_Right_To_Disconnect_Description' },
		{ dimensionId: 'connect', riskAreaKey: 'Compliance_Assessment.Dimension_Connectivity', titleKey: 'Compliance_Assessment.Control_Schedule_Send_Title', descriptionKey: 'Compliance_Assessment.Control_Schedule_Send_Description' },
		{ dimensionId: 'algo', riskAreaKey: 'Compliance_Assessment.Dimension_Algorithmic', titleKey: 'Compliance_Assessment.Control_Monitoring_Transparency_Title', descriptionKey: 'Compliance_Assessment.Control_Monitoring_Transparency_Description' },
		{ dimensionId: 'algo', riskAreaKey: 'Compliance_Assessment.Dimension_Algorithmic', titleKey: 'Compliance_Assessment.Control_Human_Review_Automation_Title', descriptionKey: 'Compliance_Assessment.Control_Human_Review_Automation_Description' },
		{ dimensionId: 'offensive', riskAreaKey: 'Compliance_Assessment.Dimension_Offensive', titleKey: 'Compliance_Assessment.Control_Anti_Harassment_Title', descriptionKey: 'Compliance_Assessment.Control_Anti_Harassment_Description' },
		{ dimensionId: 'demands', riskAreaKey: 'Compliance_Assessment.Dimension_Demands', titleKey: 'Compliance_Assessment.Control_Time_Management_Coaching_Title', descriptionKey: 'Compliance_Assessment.Control_Time_Management_Coaching_Description', scope: 'individual' },
		{ dimensionId: 'emo', riskAreaKey: 'Compliance_Assessment.Dimension_Emotional', titleKey: 'Compliance_Assessment.Control_Siffi_Counselling_Title', descriptionKey: 'Compliance_Assessment.Control_Siffi_Counselling_Description', scope: 'individual' },
		{ dimensionId: 'dev', riskAreaKey: 'Compliance_Assessment.Dimension_Development', titleKey: 'Compliance_Assessment.Control_Skills_Coaching_Title', descriptionKey: 'Compliance_Assessment.Control_Skills_Coaching_Description', scope: 'individual' },
		{ dimensionId: 'lead', riskAreaKey: 'Compliance_Assessment.Dimension_Leadership', titleKey: 'Compliance_Assessment.Control_Leadership_Coaching_Title', descriptionKey: 'Compliance_Assessment.Control_Leadership_Coaching_Description', scope: 'individual' },
		{ dimensionId: 'security', riskAreaKey: 'Compliance_Assessment.Dimension_Security', titleKey: 'Compliance_Assessment.Control_Transition_Support_Title', descriptionKey: 'Compliance_Assessment.Control_Transition_Support_Description', scope: 'individual' },
		{ dimensionId: 'worklife', riskAreaKey: 'Compliance_Assessment.Dimension_Worklife', titleKey: 'Compliance_Assessment.Control_Balance_Coaching_Title', descriptionKey: 'Compliance_Assessment.Control_Balance_Coaching_Description', scope: 'individual' },
		{ dimensionId: 'offensive', riskAreaKey: 'Compliance_Assessment.Dimension_Offensive', titleKey: 'Compliance_Assessment.Control_Confidential_Support_Title', descriptionKey: 'Compliance_Assessment.Control_Confidential_Support_Description', scope: 'individual' }
	];

	complianceRows = [
		{ id: 'assessment', labelKey: 'Compliance_Assessment.Readiness_Assessment', hintKey: 'Compliance_Assessment.Readiness_Assessment_Hint' },
		{ id: 'coverage', labelKey: 'Compliance_Assessment.Readiness_Coverage', hintKey: 'Compliance_Assessment.Readiness_Coverage_Hint' },
		{ id: 'groups', labelKey: 'Compliance_Assessment.Readiness_Groups', hintKey: 'Compliance_Assessment.Readiness_Groups_Hint' },
		{ id: 'documented', labelKey: 'Compliance_Assessment.Readiness_Documented', hintKey: 'Compliance_Assessment.Readiness_Documented_Hint' },
		{ id: 'actions', labelKey: 'Compliance_Assessment.Readiness_Actions', hintKey: 'Compliance_Assessment.Readiness_Actions_Hint' },
		{ id: 'workerrep', labelKey: 'Compliance_Assessment.Readiness_Workerrep', hintKey: 'Compliance_Assessment.Worker_Representative_Checkbox_Hint' },
		{ id: 'next', labelKey: 'Compliance_Assessment.Readiness_Next', hintKey: 'Compliance_Assessment.Readiness_Next_Hint' }
	];

	constructor() {
		this.vm = this;
	}

	ngOnInit() {
		this.vm.user = this.session.getSession().user;
		this.vm.questionnaireRequestId = this.route.snapshot.paramMap.get('questionnaireRequestId');
		this.vm.actionPlanOnly = (this.route.snapshot.routeConfig?.path || '').indexOf('action-plan') !== -1;
		this.vm.complianceState = {};
		this.vm.readinessOverrides = {};
		this.vm.reviewerAssignments = this.loadReviewerAssignments();
		this.vm.workerRep = this.loadWorkerRepReview();
		this.vm.reportNarrative = '';
		this.vm.loading = true;
		this.vm.resultsViewMode = 'results';
		this.vm.reviewingCompletedPlan = this.vm.actionPlanOnly && this.isReviewActionPlanRequest();
		this.vm.planStep = this.vm.reviewingCompletedPlan ? 2 : 1;
		this.vm.openOwnerPickerKey = null;
		this.vm.ownerSearch = {};
		this.vm.load = this.load;
		this.vm.back = this.back;
		this.vm.returnToDashboard = this.returnToDashboard;
		this.vm.togglePriority = this.togglePriority;
		this.vm.toggleControl = this.toggleControl;
		this.vm.controlsForRisk = this.controlsForRisk;
		this.vm.orgControlsForRisk = this.orgControlsForRisk;
		this.vm.individualControlsForRisk = this.individualControlsForRisk;
		this.vm.addCustomControl = this.addCustomControl;
		this.vm.riskHasMissingControl = this.riskHasMissingControl;
		this.vm.riskDisplayName = this.riskDisplayName;
		this.vm.goToAssign = this.goToAssign;
		this.vm.goToPriorities = this.goToPriorities;
		this.vm.filteredEmployees = this.filteredEmployees;
		this.vm.filteredReviewerEmployees = this.filteredReviewerEmployees;
		this.vm.selectOwner = this.selectOwner;
		this.vm.selectReviewer = this.selectReviewer;
		this.vm.reviewerEmployeeName = this.reviewerEmployeeName;
		this.vm.saveAction = this.saveAction;
		this.vm.completeActionPlan = this.completeActionPlan;
		this.vm.confirmAssignmentsAndSendInvitations = this.confirmAssignmentsAndSendInvitations;
		this.vm.acceptActionPlan = this.acceptActionPlan;
		this.vm.pendingInvitationCount = this.pendingInvitationCount;
		this.vm.sentInvitationCount = this.sentInvitationCount;
		this.vm.shouldShowInvitationSendStage = this.shouldShowInvitationSendStage;
		this.vm.assignmentRows = this.assignmentRows;
		this.vm.latestControlDueDate = this.latestControlDueDate;
		this.vm.includedActions = this.includedActions;
		this.vm.selectedActions = this.selectedActions;
		this.vm.actionsForRisk = this.actionsForRisk;
		this.vm.actionTitle = this.actionTitle;
		this.vm.actionDescription = this.actionDescription;
		this.vm.actionStatus = this.actionStatus;
		this.vm.controlScopeLabel = this.controlScopeLabel;
		this.vm.assignmentStatusLabel = this.assignmentStatusLabel;
		this.vm.publicActionUrl = this.publicActionUrl;
		this.vm.copyPublicActionUrl = this.copyPublicActionUrl;
		this.vm.refreshTracking = this.refreshTracking;
		this.vm.readinessStateLabel = this.readinessStateLabel;
		this.vm.actionDoneCount = this.actionDoneCount;
		this.vm.toggleActionDone = this.toggleActionDone;
		this.vm.saveTrackingNote = this.saveTrackingNote;
		this.vm.saveAllTracking = this.saveAllTracking;
		this.vm.addTrackingDocuments = this.addTrackingDocuments;
		this.vm.removeTrackingDocument = this.removeTrackingDocument;
		this.vm.assessmentDate = this.assessmentDate;
		this.vm.nextReviewDate = this.nextReviewDate;
		this.vm.nextAnnualAssessmentDate = this.nextAnnualAssessmentDate;
		this.vm.assessmentYear = this.assessmentYear;
		this.vm.progressTracking = this.progressTracking;
		this.vm.reviewActionPlan = this.reviewActionPlan;
		this.vm.viewComplianceRecord = this.viewComplianceRecord;
		this.vm.viewFullReport = this.viewFullReport;
		this.vm.generateFullAiReport = this.generateFullAiReport;
		this.vm.viewResults = this.viewResults;
		this.vm.reportSummary = this.reportSummary;
		this.vm.reportCredibilitySentence = this.reportCredibilitySentence;
		this.vm.teamParticipationInline = this.teamParticipationInline;
		this.vm.riskFinding = this.riskFinding;
		this.vm.reportConclusion = this.reportConclusion;
		this.vm.saveReportNarrative = this.saveReportNarrative;
		this.vm.printPage = this.printPage;
		this.vm.riskClass = this.riskClass;
		this.vm.readinessClass = this.readinessClass;
		this.vm.openRisk = this.openRisk;
			this.vm.closeRisk = this.closeRisk;
			this.vm.recommendedControls = this.recommendedControls;
			this.vm.updateReadinessState = this.updateReadinessState;
			this.vm.workerRepReadinessRow = this.workerRepReadinessRow;
			this.vm.saveWorkerRepReview = this.saveWorkerRepReview;
			this.vm.shouldShowPulse = this.shouldShowPulse;
			this.vm.pulseQuestionCount = this.pulseQuestionCount;
			this.vm.launchPulseSurvey = this.launchPulseSurvey;
			this.vm.compareLabel = this.compareLabel;
			this.vm.loadComparison = this.loadComparison;
			this.vm.retryComparison = this.retryComparison;
			this.vm.hasCompareScore = this.hasCompareScore;
			this.vm.previousLeft = this.previousLeft;
			this.vm.previousLabel = this.previousLabel;
			this.vm.compareChangeClass = this.compareChangeClass;
			this.vm.compareChangeLabel = this.compareChangeLabel;
			this.vm.canOpenActionDetails = this.canOpenActionDetails;
			this.vm.openActionDetail = this.openActionDetail;
			this.vm.closeActionDetail = this.closeActionDetail;
			this.vm.actionProgressNote = this.actionProgressNote;
			this.vm.actionDocuments = this.actionDocuments;
			this.vm.documentDisplayName = this.documentDisplayName;
				this.vm.legalBasisRows = this.legalBasisRows;
				this.vm.riskLevelCount = this.riskLevelCount;
				this.vm.includedPriorityRisks = this.includedPriorityRisks;
				this.vm.workerConsultationLabel = this.workerConsultationLabel;
				this.vm.teamParticipationRows = this.teamParticipationRows;
				this.vm.getComplianceAssessmentAiRecommendations = this.getComplianceAssessmentAiRecommendations;
				this.load();
			}

	load() {
		this.vm.loading = true;
		this.vm.error = false;
		var language = 'en';
		Promise.all([
			this.restService.getComplianceAssessmentSummary({ questionnaireRequestId: this.vm.questionnaireRequestId, language: language }),
			this.safePromise(this.restService.getComplianceAssessmentActions({ questionnaireRequestId: this.vm.questionnaireRequestId }), []),
			this.safePromise(this.restService.getComplianceAssessmentState({ questionnaireRequestId: this.vm.questionnaireRequestId }), {})
		]).then((values: any[]) => {
			this.vm.summary = values[0] || {};
			this.vm.actions = values[1] || [];
			this.vm.complianceState = values[2] || {};
			this.vm.readinessOverrides = this.vm.complianceState.readinessOverrides || {};
			this.vm.reviewerAssignments = this.vm.complianceState.reviewerAssignments || this.loadReviewerAssignments();
			this.vm.workerRep = this.vm.complianceState.workerRep || this.loadWorkerRepReview();
			this.vm.reportNarrative = this.vm.complianceState.reportNarrative || '';
			this.vm.employees = [];
			this.prepareData();
			this.vm.loading = false;
			this.loadEmployees();
			this.loadComparisonOptions();
		}).catch((err) => {
			console.error(err);
			this.vm.error = true;
			this.vm.loading = false;
		});
	}

	loadEmployees() {
		this.loadEmployeePage(0, []).then((employees) => {
			this.vm.employees = employees;
			(this.vm.actionDrafts || []).forEach((action) => {
				action.owner = this.normalizeActionUser(action, 'owner');
				action.reviewer = this.normalizeActionUser(action, 'reviewer');
				if (!action.ownerSearch || action.owner?.email === action.ownerSearch) {
					action.ownerSearch = this.employeeName(action.owner);
				}
			});
			this.refreshActionViewState();
		}).catch((err) => {
			console.warn('Compliance assessment employees could not be loaded', err);
			this.vm.employees = [];
		});
	}

	loadEmployeePage(start, loaded) {
		return this.businessRestServices.getWorkers({
			start: start,
			pageSize: this.employeePageSize,
			orderBy: 'name',
			ascending: true
		}).then((employees) => {
			employees = employees || [];
			var nextLoaded = loaded.concat(employees);
			if (employees.length < this.employeePageSize) return nextLoaded;
			return this.loadEmployeePage(start + employees.length, nextLoaded);
		});
	}

	safePromise(promise, fallback) {
		return promise.catch((err) => {
			console.warn('Optional compliance assessment data could not be loaded', err);
			return fallback;
		});
	}

	prepareData() {
		var survey = this.vm.summary?.survey || {};
		var calculated = this.vm.summary?.calculatedResults || {};
		var metrics = calculated.metrics || {};
		var sent = metrics.sentCount || survey.sentCount || survey.totalCount || 0;
		var responded = metrics.respondedCount || survey.respondedCount || survey.completedCount || 0;
		this.vm.responseRate = metrics.responseRate !== undefined ? metrics.responseRate : (sent > 0 ? Math.round((responded * 100) / sent) : 0);
		this.vm.respondedCount = responded;
		this.vm.sentCount = sent;
		this.vm.credibleResponseRate = metrics.credibleResponseRate || 60;
		this.vm.risks = calculated.risks || this.buildRisks();
		this.vm.priorities = this.buildPriorityRisks(calculated.priorities);
		this.vm.reportableRows = metrics.reportableRows !== undefined ? metrics.reportableRows : (this.vm.risks || []).filter((risk) => !risk.suppressed).length;
		this.vm.compareId = this.vm.compareId || '';
		this.vm.compareOptions = this.buildCompareOptions();
		this.vm.readinessOverrides = this.vm.readinessOverrides || {};
		this.vm.readiness = this.buildReadiness(calculated.readiness);
		this.updateReadinessCounts();
		this.vm.actionDrafts = this.buildActionDrafts();
		this.refreshActionViewState();
		var assignedPlanComplete = this.includedActions().length > 0 && this.includedActions().every((action) => action.owner && action.cutOffDate);
		this.vm.planCompleted = ((this.vm.complianceState && this.vm.complianceState.planCompleted === true) || this.serverPlanCompleted()) && assignedPlanComplete;
		if (this.vm.reviewingCompletedPlan) this.vm.planStep = 2;
	}

	buildPriorityRisks(serverPriorities) {
		const priorities = new Map<string, any>();
		[...(serverPriorities || []), ...(this.vm.risks || [])].forEach((risk) => {
			if (!risk || risk.suppressed || priorities.has(risk.id)
				|| !(this.isPriority(risk.riskLevel) || risk.isPriority || risk.confidentialActionRequired)) return;
			// Older summaries mark moderate risks as excluded because they were not planning priorities.
			if (this.isPriority(risk.riskLevel) && !risk.isPriority && risk.included === false) risk.included = true;
			risk.isPriority = true;
			if (risk.included === undefined || risk.included === null) risk.included = true;
			priorities.set(risk.id, risk);
		});
		return Array.from(priorities.values()).sort((a, b) => this.riskWeight(b.riskLevel) - this.riskWeight(a.riskLevel));
	}

	buildRisks() {
		var questionScores = this.vm.summary?.questionScores || [];
		var risks: any[] = this.dimensions.map((dimension) => {
			var scores = questionScores
				.filter((questionScore) => this.questionBelongsToDimension(questionScore.question, dimension.id))
				.map((questionScore) => Number(questionScore.averageScore))
				.filter((score) => !isNaN(score));
			var averageScore = scores.length ? Math.round((scores.reduce((sum, score) => sum + score, 0) / scores.length) * 10) / 10 : null;
			var riskLevel = averageScore === null ? 'NA' : this.calculateDimensionRiskLevel(averageScore, dimension);
			var confidentialActionRequired = !!dimension.exposure && averageScore !== null && averageScore > 0;
			var priority = this.isPriority(riskLevel) || confidentialActionRequired;
			return {
				id: dimension.id,
				name: this.dimensionFallbackName(dimension.id),
				nameKey: dimension.nameKey,
				descKey: dimension.descKey,
				source: dimension.source,
				benchmark: dimension.benchmark,
				referenceMean: dimension.benchmark,
				referenceSd: dimension.referenceSd,
				zScore: averageScore !== null && !dimension.exposure && dimension.referenceSd ? Math.round(((averageScore - dimension.benchmark) / dimension.referenceSd) * 10) / 10 : null,
				exposure: dimension.exposure,
				confidentialActionRequired: confidentialActionRequired,
				riskLevel: riskLevel,
				userCount: this.vm.respondedCount,
					averageScore: averageScore,
					isPriority: priority,
					included: priority
				};
		});
		return risks.sort((a, b) => this.riskWeight(b.riskLevel) - this.riskWeight(a.riskLevel));
	}

	loadComparisonOptions() {
		const request = ++this.comparisonListRequest;
		this.comparisonCache.clear();
		this.vm.previousAssessments = [];
		this.vm.compareId = '';
		this.loadComparison();
		this.vm.compareOptions = this.buildCompareOptions();
		this.vm.comparisonListLoading = true;
		this.vm.comparisonListError = false;
		return this.restService.getPreviousComplianceAssessments({ questionnaireRequestId: this.vm.questionnaireRequestId }).then((surveys) => {
			if (request !== this.comparisonListRequest) return;
			this.vm.previousAssessments = surveys || [];
			this.vm.compareOptions = this.buildCompareOptions();
		}).catch((err) => {
			if (request !== this.comparisonListRequest) return;
			console.warn('Previous compliance assessments could not be loaded', err);
			this.vm.comparisonListError = true;
		}).finally(() => {
			if (request === this.comparisonListRequest) this.vm.comparisonListLoading = false;
		});
	}

	buildCompareOptions() {
		return [
			{ id: '', label: this.translate('Compliance_Assessment.Current_Only') },
			...(this.vm.previousAssessments || []).map((survey) => ({
				id: String(survey.questionnaireRequestId || survey.requestId || survey.questionnaireRequest?.id || survey.id),
				label: this.translate('Compliance_Assessment.Annual_Assessment') + ' · '
					+ new Date(survey.completedAt || survey.startDate || survey.created || survey.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
					+ (survey.groupName ? ' · ' + survey.groupName : '')
			}))
		];
	}

	loadComparison() {
		const request = ++this.comparisonRequest;
		const id = this.vm.compareId;
		this.vm.comparisonRisks = [];
		this.vm.comparisonError = false;
		this.vm.comparisonLoading = false;
		if (!id) return Promise.resolve();
		if (!(this.vm.previousAssessments || []).some((survey) =>
			String(survey.questionnaireRequestId || survey.requestId || survey.questionnaireRequest?.id || survey.id) === id)) return Promise.resolve();
		this.vm.comparisonLoading = true;
		if (!this.comparisonCache.has(id)) {
			const result = this.restService.getComplianceAssessmentSummary({ questionnaireRequestId: id, language: 'en' })
				.then((summary) => {
					if (!Array.isArray(summary?.calculatedResults?.risks)) throw new Error('Previous assessment scores are unavailable');
					return summary.calculatedResults.risks;
				}).catch((err) => {
					this.comparisonCache.delete(id);
					throw err;
				});
			this.comparisonCache.set(id, result);
		}
		return this.comparisonCache.get(id).then((risks) => {
			if (request === this.comparisonRequest) this.vm.comparisonRisks = risks;
		}).catch((err) => {
			if (request !== this.comparisonRequest) return;
			console.warn('Compliance assessment comparison could not be loaded', err);
			this.vm.comparisonError = true;
		}).finally(() => {
			if (request === this.comparisonRequest) this.vm.comparisonLoading = false;
		});
	}

	retryComparison() {
		return this.vm.comparisonListError ? this.loadComparisonOptions() : this.loadComparison();
	}

	compareLabel() {
		var selected = (this.vm.compareOptions || []).find((option) => option.id === this.vm.compareId);
		return selected ? selected.label : '';
	}

	previousScore(risk) {
		if (!this.vm.compareId || this.vm.comparisonLoading || this.vm.comparisonError || !risk || risk.suppressed
			|| risk.averageScore === null || risk.averageScore === undefined || risk.averageScore === ''
			|| !Number.isFinite(Number(risk.averageScore))) return null;
		const previous = (this.vm.comparisonRisks || []).find((row) => row.id === risk.id);
		if (!previous || previous.suppressed || previous.averageScore === null || previous.averageScore === undefined
			|| previous.averageScore === '' || !!previous.exposure !== !!risk.exposure) return null;
		const score = Number(previous.averageScore);
		return Number.isFinite(score) && score >= 0 && score <= 100 ? score : null;
	}

	hasCompareScore(risk) {
		return this.previousScore(risk) !== null;
	}

	previousLeft(risk) {
		var previous = this.previousScore(risk);
		if (previous === null) return 0;
		return Math.max(0, Math.min(100, risk?.exposure ? previous * 4 : previous));
	}

	previousLabel(risk) {
		var previous = this.previousScore(risk);
		if (previous === null) return '';
		return this.translate('Compliance_Assessment.Was') + ' ' + (risk?.exposure ? Math.round(previous) + '%' : Math.round(previous));
	}

	compareChange(risk) {
		var previous = this.previousScore(risk);
		if (previous === null || risk?.averageScore === null || risk?.averageScore === undefined) return null;
		var current = Number(risk.averageScore);
		return risk?.exposure ? Math.round(previous) - Math.round(current) : Math.round(current) - Math.round(previous);
	}

	compareChangeClass(risk) {
		var change = this.compareChange(risk);
		if (change === null || change === 0) return 'flat';
		return change > 0 ? 'up' : 'down';
	}

	compareChangeLabel(risk) {
		var change = this.compareChange(risk);
		if (change === null) return '';
		if (change === 0) return '→ 0';
		if (risk?.exposure) return (change > 0 ? '▼ ' : '▲ ') + Math.abs(change) + 'pp';
		return (change > 0 ? '▲ +' : '▼ ') + change;
	}

	buildReadiness(calculatedReadiness?) {
		const actions = (this.vm.actions || []).filter((action) => !!action);
		const complete = {
			assessment: this.vm.summary?.survey?.status === 'CLOSED',
			coverage: this.vm.responseRate >= this.vm.credibleResponseRate,
			groups: (this.vm.risks || []).length > 0,
			documented: (this.vm.priorities || []).length > 0,
			actions: actions.length > 0 && actions.every((action) => action.owner && action.cutOffDate),
			next: false
		};
		return this.complianceRows.map((row) => {
			const calculated = calculatedReadiness?.rows?.find((item) => item.id === row.id) || {};
			return {
				...row,
				...calculated,
				state: row.id === 'workerrep'
					? (this.workerRepresentativeReviewed() ? 'complete' : 'incomplete')
					: (this.vm.readinessOverrides?.[row.id] || calculated.state || (complete[row.id] ? 'complete' : 'incomplete'))
			};
		});
	}

	updateReadinessState(row, state) {
		if (!row) return;
		row.state = state;
		this.vm.readinessOverrides = this.vm.readinessOverrides || {};
		this.vm.readinessOverrides[row.id] = state;
		this.saveComplianceState();
		this.updateReadinessCounts();
	}

	updateReadinessCounts() {
		this.vm.readinessCompleteCount = (this.vm.readiness || []).filter((row) => row.state === 'complete').length;
		this.vm.readinessApplicableCount = (this.vm.readiness || []).filter((row) => row.state !== 'na').length;
		this.vm.complianceReady = this.vm.readinessApplicableCount > 0 && this.vm.readinessCompleteCount === this.vm.readinessApplicableCount;
	}

	loadReadinessOverrides() {
		return {};
	}

	saveReadinessOverrides() {
		this.vm.readinessOverrides = this.vm.readinessOverrides || {};
		return this.saveComplianceState();
	}

	buildActionDrafts() {
		var uniqueActions = this.dedupeActions(this.vm.actions || []);
		this.vm.actions = uniqueActions;
		var drafts = uniqueActions.map((action) => ({
			...action,
			dimensionId: this.inferDimensionId(action.riskArea, action),
			titleKey: this.findControlTemplateByTitle(action.title)?.titleKey,
			descriptionKey: this.findControlTemplateByTitle(action.title)?.descriptionKey,
			owner: this.normalizeActionUser(action, 'owner'),
			reviewer: this.normalizeActionUser(action, 'reviewer'),
			ownerSearch: this.employeeName(this.normalizeActionUser(action, 'owner')),
			cutOffDate: this.dateInputValue(action.cutOffDate) || this.defaultActionDueDate(),
			successIndicator: this.successIndicatorFromDescription(action.description),
			progressNote: action.ownerInput || '',
			documents: action.documents || action.documentNames || [],
			done: !!action.ownerSubmittedAt || String(action.status || '').toUpperCase() === 'SUBMITTED' || String(action.status || '').toUpperCase() === 'REVIEW_ACCEPTED' || String(action.status || '').toUpperCase() === 'HR_ACCEPTED',
			included: true,
			controlKey: this.actionLogicalKey(action)
		}));
		(this.vm.priorities || []).forEach((priority) => {
			this.controlTemplates
				.filter((control) => control.dimensionId === priority.id)
				.forEach((control) => {
					var title = this.controlTemplateTitle(control);
					var description = this.controlTemplateDescription(control);
					var controlKey = priority.id + '::' + control.titleKey;
					var exists = drafts.some((draft) => this.actionLogicalKey(draft) === controlKey || (draft.dimensionId === priority.id && (draft.titleKey === control.titleKey || draft.title === title)));
					if (!exists) {
						drafts.push({
							controlKey: controlKey,
							questionnaireRequestId: this.vm.questionnaireRequestId,
							dimensionId: priority.id,
							title: title,
							titleKey: control.titleKey,
							description: description,
							descriptionKey: control.descriptionKey,
							riskArea: this.controlTemplateRiskArea(control),
							riskAreaKey: control.riskAreaKey,
							cutOffDate: this.defaultActionDueDate(),
							owner: null,
							reviewer: null,
							status: 'DRAFT',
							successIndicator: this.defaultSuccessIndicator(this.riskDisplayName(priority)),
							progressNote: '',
							documents: [],
							done: false,
							scope: control.scope || 'organisational',
							included: false
						});
					}
				});
		});
		return this.dedupeActions(drafts);
	}

	togglePriority(risk) {
		risk.included = !risk.included;
		if (!risk.included) {
			this.controlsForRisk(risk).forEach((draft) => draft.included = false);
		}
		this.refreshActionViewState();
		this.vm.planErrorKey = null;
	}

	toggleControl(action) {
		action.included = !action.included;
		this.refreshActionViewState();
		this.vm.planErrorKey = null;
	}

	controlsForRisk(risk) {
		return (this.vm.actionDrafts || []).filter((action) => action.dimensionId === risk.id || this.sameRiskArea(action.riskArea, this.riskDisplayName(risk)));
	}

	orgControlsForRisk(risk) {
		return this.controlsForRisk(risk).filter((action) => (action.scope || 'organisational') !== 'individual');
	}

	individualControlsForRisk(risk) {
		return this.controlsForRisk(risk).filter((action) => (action.scope || 'organisational') === 'individual');
	}

	addCustomControl(risk) {
		var title = (risk.customControlTitle || '').trim();
		if (!title) {
			risk.customControlMissing = true;
			return;
		}
		risk.customControlMissing = false;
		var dimensionId = risk.id;
		this.vm.actionDrafts.push({
			controlKey: dimensionId + '::custom::' + Date.now(),
			questionnaireRequestId: this.vm.questionnaireRequestId,
			dimensionId: dimensionId,
			title: title,
			description: this.translate('Compliance_Assessment.Custom_Control'),
			riskArea: this.riskDisplayName(risk),
			cutOffDate: this.defaultActionDueDate(),
			owner: null,
			reviewer: null,
			status: 'DRAFT',
			successIndicator: this.defaultSuccessIndicator(this.riskDisplayName(risk)),
			progressNote: '',
			documents: [],
			done: false,
			custom: true,
			scope: 'custom',
			included: true
		});
		risk.customControlTitle = '';
		this.refreshActionViewState();
		this.vm.planErrorKey = null;
	}

	riskHasMissingControl(risk) {
		return !!risk.controlMissing;
	}

	goToAssign() {
		var missing = (this.vm.priorities || []).filter((risk) => risk.included && !this.controlsForRisk(risk).some((action) => action.included));
		(this.vm.priorities || []).forEach((risk) => risk.controlMissing = missing.indexOf(risk) !== -1);
		if (missing.length) {
			this.vm.planErrorKey = 'Compliance_Assessment.Action_Plan_Missing_Control';
			return;
		}
		this.vm.planErrorKey = null;
		this.rememberProcessPage();
		this.vm.planStep = 2;
	}

	goToPriorities() {
		this.vm.planStep = 1;
	}

	filteredEmployees(action) {
		var term = (action.ownerSearch || '').toLowerCase().trim();
		return (this.vm.employees || []).filter((employee) => {
			var text = (this.employeeName(employee) + ' ' + (employee.email || '')).toLowerCase();
			return !term || text.indexOf(term) !== -1;
		}).slice(0, 8);
	}

	selectOwner(action, employee) {
		action.owner = this.userFromEmployee(employee);
		action.ownerSearch = this.employeeName(employee);
		action.ownerInvitePending = false;
		action.validationMissing = false;
		this.refreshActionViewState();
		this.vm.openOwnerPickerKey = null;
	}

	filteredReviewerEmployees(role) {
		var assignment = (this.vm.reviewerAssignments || {})[role] || {};
		var term = (assignment.search || '').toLowerCase().trim();
		return (this.vm.employees || []).filter((employee) => {
			var text = (this.employeeName(employee) + ' ' + (employee.email || '') + ' ' + this.employeeRole(employee)).toLowerCase();
			return !term || text.indexOf(term) !== -1;
		}).slice(0, 8);
	}

	selectReviewer(role, employee) {
		this.vm.reviewerAssignments = this.vm.reviewerAssignments || {};
		this.vm.reviewerAssignments[role] = {
			user: this.userFromEmployee(employee),
			search: this.employeeName(employee),
			selectedAt: new Date().toISOString()
		};
		this.vm.openReviewerPickerKey = null;
		this.saveReviewerAssignments();
		this.vm.readiness = this.buildReadiness();
		this.updateReadinessCounts();
	}

	reviewerEmployeeName(role) {
		var assignment = (this.vm.reviewerAssignments || {})[role] || {};
		return this.employeeName(assignment.user);
	}

	saveAction(action, skipPrepare = false) {
		var ownerInput = action.progressNote !== undefined ? action.progressNote : action.ownerInput;
		var documents = (action.documents || []).slice();
		var status = action.id ? action.status : 'ASSIGNED';
		var ownerSubmittedAt = action.ownerSubmittedAt;
		var owner = action.owner;
		var reviewer = action.reviewer;
		action.ownerInput = ownerInput;
		action.documents = documents;
		var payload = {
			id: action.id,
			questionnaireRequestId: this.vm.questionnaireRequestId,
			title: action.title,
			description: this.withSuccessIndicator(action.description, action.successIndicator),
			riskArea: action.riskArea,
			cutOffDate: this.toServerDate(this.dateInputValue(action.cutOffDate) || action.cutOffDate || this.defaultActionDueDate()),
			owner: this.normalizeUserForSave(action.owner),
			reviewer: this.normalizeUserForSave(action.reviewer),
			status: status,
			ownerToken: action.ownerToken,
			reviewerToken: action.reviewerToken,
			ownerInput: ownerInput,
			reviewerInput: action.reviewerInput,
			hrInput: action.hrInput,
			ownerSubmittedAt: ownerSubmittedAt,
			reviewerAcceptedAt: action.reviewerAcceptedAt,
			hrAcceptedAt: action.hrAcceptedAt,
			documents: documents
		};
		action.saving = true;
		return this.restService.saveComplianceAssessmentAction({ questionnaireRequestId: this.vm.questionnaireRequestId }, payload).then((saved) => {
			Object.assign(action, saved || {});
			action.cutOffDate = this.dateInputValue(action.cutOffDate) || this.defaultActionDueDate();
			action.ownerInput = ownerInput;
			action.progressNote = ownerInput || '';
			action.documents = documents;
			action.status = status;
			action.ownerSubmittedAt = ownerSubmittedAt;
			action.owner = action.owner || owner;
			action.reviewer = action.reviewer || reviewer;
			action.owner = this.normalizeActionUser(action, 'owner');
			action.reviewer = this.normalizeActionUser(action, 'reviewer');
			action.ownerSearch = this.employeeName(action.owner);
			action.done = !!action.ownerSubmittedAt || String(action.status || '').toUpperCase() === 'SUBMITTED' || String(action.status || '').toUpperCase() === 'REVIEW_ACCEPTED' || String(action.status || '').toUpperCase() === 'HR_ACCEPTED';
			action.ownerInvitePending = false;
			action.saving = false;
			this.replaceSavedAction(action);
			this.refreshActionViewState();
			if (!skipPrepare) this.prepareData();
			return action;
		}).catch((err) => {
			console.error(err);
			action.saving = false;
			throw err;
		});
	}

	replaceSavedAction(savedAction) {
		if (!savedAction || !savedAction.id) return;
		this.vm.actions = this.vm.actions || [];
		var index = this.vm.actions.findIndex((existing) => existing.id === savedAction.id);
		if (index >= 0) {
			this.vm.actions[index] = savedAction;
		} else {
			this.vm.actions.push(savedAction);
		}
		this.vm.actions = this.dedupeActions(this.vm.actions);
	}

	includedActions() {
		return this.vm.selectedActionList || this.computeSelectedActions();
	}

	selectedActions() {
		return this.vm.selectedActionList || this.computeSelectedActions();
	}

	computeSelectedActions() {
		return (this.vm.actionDrafts || []).filter((action) => {
			if (!action) return false;
			if (!action.included) return false;
			var risk = (this.vm.priorities || []).find((priority) => action.dimensionId === priority.id || this.sameRiskArea(action.riskArea, this.riskDisplayName(priority)));
			return !risk || risk.included;
		});
	}

	refreshActionViewState() {
		this.vm.selectedActionList = this.computeSelectedActions();
		this.vm.assignmentRowList = this.buildAssignmentRows(this.vm.selectedActionList);
	}

	actionsForRisk(risk) {
		return this.selectedActions().filter((action) => action && (action.dimensionId === risk.id || this.sameRiskArea(action.riskArea, this.riskDisplayName(risk))));
	}

	actionTitle(action) {
		if (!action) return '';
		return action.titleKey ? this.translateOrFallback(action.titleKey, action.title) : action.title;
	}

	actionDescription(action) {
		if (!action) return '';
		return action.descriptionKey ? this.translateOrFallback(action.descriptionKey, action.description) : action.description;
	}

	controlScopeLabel(action) {
		if (action && action.custom) return this.translate('Compliance_Assessment.Custom');
		if (action && action.scope === 'individual') return this.translate('Compliance_Assessment.Individual');
		return this.translate('Compliance_Assessment.Organisational');
	}

	actionStatus(action) {
		var value = String(action?.status || '').toUpperCase();
		if (action?.done || action?.ownerSubmittedAt || value === 'SUBMITTED' || value === 'REVIEW_ACCEPTED' || value === 'HR_ACCEPTED') {
			return { className: 'low', labelKey: 'Compliance_Assessment.Action_Status_Completed' };
		}
		if ((action?.progressNote && action.progressNote.trim()) || (action?.ownerInput && action.ownerInput.trim()) || (this.actionDocuments(action).length > 0)) {
			return { className: 'moderate', labelKey: 'Compliance_Assessment.Action_Status_In_Progress' };
		}
		if (action?.ownerToken) {
			return { className: 'moderate', labelKey: 'Compliance_Assessment.Action_Status_Shared' };
		}
		if (action?.owner) {
			return { className: 'low', labelKey: 'Compliance_Assessment.Action_Status_Assigned' };
		}
		return { className: 'na', labelKey: 'Compliance_Assessment.Action_Status_Unassigned' };
	}

	canOpenActionDetails(action) {
		return this.actionStatus(action).labelKey === 'Compliance_Assessment.Action_Status_Completed';
	}

	openActionDetail(action) {
		if (!this.canOpenActionDetails(action)) return;
		this.vm.selectedActionDetail = action;
	}

	closeActionDetail() {
		this.vm.selectedActionDetail = null;
	}

	actionProgressNote(action) {
		return (action?.progressNote || action?.ownerInput || '').trim();
	}

	actionDocuments(action) {
		return [...(action?.documentFiles || []), ...(action?.documents || action?.documentNames || [])].filter((document) => !!document);
	}

	documentDisplayName(document) {
		if (!document) return '';
		if (typeof document === 'string') return document;
		return document.name || document.fileName || document.filename || document.title || '';
	}

	legalBasisRows() {
		return [
			{ titleKey: 'Compliance_Assessment.Legal_Basis_Periodic_Title', textKey: 'Compliance_Assessment.Legal_Basis_Periodic_Text' },
			{ titleKey: 'Compliance_Assessment.Legal_Basis_Coverage_Title', textKey: 'Compliance_Assessment.Legal_Basis_Coverage_Text' },
			{ titleKey: 'Compliance_Assessment.Legal_Basis_Participation_Title', textKey: 'Compliance_Assessment.Legal_Basis_Participation_Text' },
			{ titleKey: 'Compliance_Assessment.Legal_Basis_Documented_Title', textKey: 'Compliance_Assessment.Legal_Basis_Documented_Text' }
		];
	}

	riskLevelCount(level) {
		var normalized = this.riskClass(level);
		return (this.vm.risks || []).filter((risk) => this.riskClass(risk.riskLevel) === normalized).length;
	}

	includedPriorityRisks() {
		return (this.vm.priorities || []).filter((risk) => risk && risk.included !== false);
	}

	workerRepresentativeReviewed() {
		return this.vm.workerRep?.consulted === true;
	}

	workerConsultationLabel() {
		const reviewed = this.workerRepresentativeReviewed();
		return this.translate(reviewed ? 'Compliance_Assessment.Complete' : 'Compliance_Assessment.Incomplete')
			+ (reviewed && this.vm.workerRep?.name ? ' · ' + this.vm.workerRep.name : '');
	}

	teamParticipationRows() {
		var calculated = this.vm.summary?.calculatedResults || {};
		var survey = this.vm.summary?.survey || {};
		var rows = calculated.teamBreakdown || calculated.teams || calculated.groupBreakdown || survey.teamBreakdown || survey.teams || [];
		if (!rows.length) {
			return [{
				name: survey.groupName || this.translate('Compliance_Assessment.Organisation'),
				responded: this.vm.respondedCount || 0,
				suppressed: false
			}];
		}
		return rows.map((row) => {
			var responded = row.responded || row.respondedCount || row.completedCount || row.count || 0;
			return {
				name: row.name || row.team || row.groupName || row.label || this.translate('Compliance_Assessment.Team'),
				responded: responded,
				suppressed: row.suppressed || responded < 5
			};
		});
	}

	assignmentStatusLabel(row) {
		return this.translate(row && row.statusKey ? row.statusKey : 'Compliance_Assessment.Assignment_Status_Ready_To_Save');
	}

	readinessStateLabel(state) {
		if (state === 'complete') return this.translate('Compliance_Assessment.Complete');
		if (state === 'na') return this.translate('Compliance_Assessment.Not_Applicable');
		return this.translate('Compliance_Assessment.Incomplete');
	}

	translate(key) {
		return this.angularFilterHelperService.translate(key);
	}

	translateOrFallback(key, fallback) {
		var value = this.translate(key);
		return value === key ? fallback : value;
	}

	controlTemplateTitle(control) {
		return control ? this.translate(control.titleKey) : '';
	}

	controlTemplateDescription(control) {
		return control ? this.translate(control.descriptionKey) : '';
	}

	controlTemplateRiskArea(control) {
		return control ? this.translate(control.riskAreaKey) : '';
	}

	findControlTemplateByTitle(title) {
		if (!title) return null;
		var normalized = this.normalizeText(title);
		return (this.controlTemplates || []).find((control) => this.normalizeText(this.controlTemplateTitle(control)) === normalized) || null;
	}

	assignmentRows() {
		return this.vm.assignmentRowList || this.buildAssignmentRows(this.includedActions());
	}

	buildAssignmentRows(actions) {
		return (actions || []).filter((action) => action && action.owner).map((action) => ({
			employeeName: this.employeeName(action.owner),
			email: action.owner.email,
			title: this.actionTitle(action),
			riskArea: action.riskArea,
			dueDate: action.cutOffDate,
			link: this.publicActionUrl('owner', action.ownerToken),
			statusKey: this.actionStatus(action).labelKey,
			action: action
		}));
	}

	latestControlDueDate() {
		var dates = this.includedActions().filter((action) => !!action).map((action) => action.cutOffDate).filter(Boolean).sort();
		return dates.length ? dates[dates.length - 1] : this.defaultActionDueDate();
	}

	pendingInvitationCount() {
		return this.includedActions().filter((action) => action && action.owner && !action.ownerToken).length;
	}

	sentInvitationCount() {
		return this.includedActions().filter((action) => action && action.ownerToken).length;
	}

	allIncludedActionsAssigned() {
		var included = this.includedActions().filter((action) => !!action);
		return included.length > 0 && included.every((action) => action.owner && action.cutOffDate);
	}

	shouldShowInvitationSendStage() {
		return !(this.allIncludedActionsAssigned() && this.pendingInvitationCount() === 0 && this.sentInvitationCount() > 0);
	}

	completeActionPlan() {
		return this.confirmAssignmentsAndSendInvitations();
	}

	confirmAssignmentsAndSendInvitations() {
		var included = this.includedActions();
		var missing = included.filter((action) => !action.owner || !action.cutOffDate);
		(this.vm.actionDrafts || []).forEach((action) => action.validationMissing = missing.indexOf(action) !== -1);
		if (!included.length) {
			this.vm.planErrorText = 'Compliance_Assessment.Error_Choose_Control_Before_Complete';
			return;
		}
		if (missing.length) {
			this.vm.planErrorText = 'Compliance_Assessment.Error_Owner_Due_Required';
			return;
		}
		this.vm.planErrorText = null;
		this.vm.completingPlan = true;
		var savedActions: any[] = [];
		var actions = this.dedupeActions(included).map((action) => this.actionPayload(action));
		this.restService.sendComplianceAssessmentActionInvitations({ questionnaireRequestId: this.vm.questionnaireRequestId }, actions).then((result: any[]) => {
			savedActions = result || [];
			this.vm.actions = this.dedupeActions((this.vm.actions || []).filter((existing) => !savedActions.some((saved) => saved.id && saved.id === existing.id)).concat(savedActions));
			(this.vm.actionDrafts || []).forEach((draft) => {
				var saved = savedActions.find((item) => this.actionLogicalKey(item) === this.actionLogicalKey(draft) || (draft.id && item.id === draft.id));
				if (saved) {
					Object.assign(draft, saved);
					draft.cutOffDate = this.dateInputValue(draft.cutOffDate) || this.defaultActionDueDate();
					draft.owner = this.normalizeActionUser(draft, 'owner');
					draft.ownerSearch = this.employeeName(draft.owner);
					draft.done = !!draft.ownerSubmittedAt || String(draft.status || '').toUpperCase() === 'SUBMITTED' || String(draft.status || '').toUpperCase() === 'REVIEW_ACCEPTED' || String(draft.status || '').toUpperCase() === 'HR_ACCEPTED';
				}
			});
			this.vm.completingPlan = false;
			this.refreshActionViewState();
			this.vm.readiness = this.buildReadiness();
			this.updateReadinessCounts();
			this.vm.planCompleted = false;
			this.vm.reviewingCompletedPlan = false;
			this.vm.actionPlanOnly = true;
			this.vm.planStep = 2;
			this.saveComplianceState();
			this.location.replaceState('/compliance/' + this.vm.questionnaireRequestId + '/action-plan');
		}).catch((err) => {
			console.error(err);
			this.vm.completingPlan = false;
			this.vm.planErrorText = 'Compliance_Assessment.Error_Save_Action_Plan';
		});
	}

	acceptActionPlan() {
		if (this.shouldShowInvitationSendStage()) {
			this.vm.planErrorText = 'Compliance_Assessment.Error_Owner_Due_Required';
			return;
		}
		this.vm.completingPlan = true;
		this.saveIncludedActions().then(() => {
			this.rememberProcessPage();
			this.savePlanCompleted(true);
			this.vm.planCompleted = true;
			this.vm.reviewingCompletedPlan = false;
			this.vm.actionPlanOnly = false;
			this.vm.readiness = this.buildReadiness();
			this.updateReadinessCounts();
			this.vm.completingPlan = false;
			this.clearReviewActionPlanRequest();
			this.location.replaceState('/compliance/' + this.vm.questionnaireRequestId + '/results');
			this.viewComplianceRecord(false);
		}).catch((err) => {
			console.error(err);
			this.vm.completingPlan = false;
			this.vm.planErrorText = 'Compliance_Assessment.Error_Save_Action_Plan';
		});
	}

	saveIncludedActions() {
		var actions = this.dedupeActions(this.includedActions());
		return actions.reduce((promise, action) => promise.then(() => this.saveAction(action, true)), Promise.resolve());
	}

	actionPayload(action) {
		var ownerInput = action.progressNote !== undefined ? action.progressNote : action.ownerInput;
		var documents = (action.documents || []).slice();
		action.ownerInput = ownerInput;
		action.documents = documents;
		return {
			id: action.id,
			questionnaireRequestId: this.vm.questionnaireRequestId,
			title: action.title,
			description: this.withSuccessIndicator(action.description, action.successIndicator),
			riskArea: action.riskArea,
			cutOffDate: this.toServerDate(this.dateInputValue(action.cutOffDate) || action.cutOffDate || this.defaultActionDueDate()),
			owner: this.normalizeUserForSave(action.owner),
			reviewer: this.normalizeUserForSave(action.reviewer),
			status: action.ownerSubmittedAt ? action.status : 'ASSIGNED',
			ownerToken: action.ownerToken,
			reviewerToken: action.reviewerToken,
			ownerInput: ownerInput,
			reviewerInput: action.reviewerInput,
			hrInput: action.hrInput,
			ownerSubmittedAt: action.ownerSubmittedAt,
			reviewerAcceptedAt: action.reviewerAcceptedAt,
			hrAcceptedAt: action.hrAcceptedAt,
			documents: documents
		};
	}

	actionDoneCount() {
		return this.includedActions().filter((action) => action.done).length;
	}

	toggleActionDone(action, event?) {
		action.done = event && event.target ? !!event.target.checked : !action.done;
		action.ownerSubmittedAt = action.done ? (action.ownerSubmittedAt || new Date().toISOString()) : null;
		action.status = action.done ? 'SUBMITTED' : 'ASSIGNED';
		this.saveAction(action, true);
	}

	saveTrackingNote(action) {
		action.ownerInput = action.progressNote;
		action.noteSaved = false;
		return this.saveAction(action, true).then((saved) => {
			action.noteSaved = true;
			this.showTrackingSavedToast();
			setTimeout(() => action.noteSaved = false, 1800);
			return saved;
		});
	}

	refreshTracking() {
		this.load();
	}

	saveAllTracking() {
		var actions = this.includedActions();
		this.vm.savingAllTracking = true;
		return actions.reduce((promise, action) => promise.then(() => this.saveTrackingNote(action)), Promise.resolve()).then(() => {
			this.vm.savingAllTracking = false;
			this.showTrackingSavedToast();
		}).catch((err) => {
			this.vm.savingAllTracking = false;
			throw err;
		});
	}

	showTrackingSavedToast() {
		this.vm.trackingSavedToast = true;
		if (this.vm.trackingSavedToastTimer) {
			clearTimeout(this.vm.trackingSavedToastTimer);
		}
		this.vm.trackingSavedToastTimer = setTimeout(() => {
			this.vm.trackingSavedToast = false;
		}, 1800);
	}

	publicActionUrl(role, token) {
		if (!token) return '';
		return window.location.origin + '/app/#/compliance-assessment-action/' + role + '/' + encodeURIComponent(token);
	}

	copyPublicActionUrl(url) {
		if (!url) return;
		if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
			navigator.clipboard.writeText(url).then(() => this.showTrackingSavedToast()).catch(() => {});
		}
	}

	async addTrackingDocuments(action, event) {
		const files = Array.from(event?.target?.files || []) as File[];
		if (event?.target) event.target.value = '';
		if (action.uploading || action.saving || !files.length) return;
		action.uploading = true;
		try {
			if (!action.id) await this.saveAction(action, true);
			if (!action.id) throw new Error('Action was not saved');
			for (const file of files) {
				const saved = await this.restService.uploadComplianceAssessmentDocument({ questionnaireRequestId: this.vm.questionnaireRequestId, actionItemId: action.id }, file);
				action.documentFiles = saved.documentFiles || [];
				action.documents = saved.documents || [];
				this.replaceSavedAction(action);
			}
			this.showTrackingSavedToast();
		} catch (err) {
			this.documentError(err);
		} finally {
			action.uploading = false;
		}
	}

	async removeTrackingDocument(action, document) {
		if (action.uploading || action.saving) return;
		if (!document?.id) {
			action.documents = (action.documents || []).filter((name) => name !== document);
			return this.saveAction(action, true);
		}
		action.uploading = true;
		try {
			const saved = await this.restService.removeComplianceAssessmentDocument({ questionnaireRequestId: this.vm.questionnaireRequestId, actionItemId: action.id, documentId: document.id });
			action.documentFiles = saved.documentFiles || [];
			this.replaceSavedAction(action);
		} catch (err) {
			this.documentError(err);
		} finally {
			action.uploading = false;
		}
	}

	async downloadActionDocument(action, document) {
		if (!document?.id) return;
		try {
			const result = await this.restService.getComplianceAssessmentDocumentDownload({ questionnaireRequestId: this.vm.questionnaireRequestId, actionItemId: action.id, documentId: document.id });
			window.location.assign(result.url);
		} catch (err) {
			this.documentError(err);
		}
	}

	private documentError(err) {
		console.error(err);
		this.vm.documentErrorKey = err.status === 413 ? 'Compliance_Assessment.Document_Too_Large'
			: err.status === 406 ? 'Compliance_Assessment.Document_Type_Not_Allowed' : 'Common.Error_Default';
		setTimeout(() => this.vm.documentErrorKey = null, 5000);
	}

	assessmentDate() {
		var survey = this.vm.summary?.survey || {};
		return survey.endDate || survey.closedAt || survey.updatedAt || survey.startDate || new Date().toISOString();
	}

	nextReviewDate() {
		return this.latestControlDueDate();
	}

	nextAnnualAssessmentDate() {
		var date = new Date(this.assessmentDate());
		if (isNaN(date.getTime())) return null;
		date.setFullYear(date.getFullYear() + 1);
		return date.toISOString();
	}

	assessmentYear() {
		var date = new Date(this.assessmentDate());
		return isNaN(date.getTime()) ? new Date().getFullYear() : date.getFullYear();
	}

	progressTracking() {
		this.clearReviewActionPlanRequest();
		this.angularRoutingHelperService.setPath('/compliance/' + this.vm.questionnaireRequestId + '/action-plan');
	}

	reviewActionPlan() {
		try {
			sessionStorage.setItem(this.reviewActionPlanStorageKey(), 'true');
		} catch (e) {}
		this.vm.actionPlanOnly = true;
		this.vm.reviewingCompletedPlan = true;
		this.vm.planStep = 2;
		this.angularRoutingHelperService.setPath('/compliance/' + this.vm.questionnaireRequestId + '/action-plan', false, false, { step: 'assign' });
	}

	viewComplianceRecord(rememberPage = true) {
		if (rememberPage && (this.vm.actionPlanOnly || this.vm.resultsViewMode !== 'record')) this.rememberProcessPage();
		this.vm.actionPlanOnly = false;
		this.vm.complianceRecord = this.buildComplianceRecordData();
		this.vm.resultsViewMode = 'record';
		this.vm.showComplianceRecord = true;
		setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
	}

	buildComplianceRecordData() {
		var includedActions = this.includedActions();
		return {
			legalBasisRows: this.legalBasisRows(),
			riskLevelCounts: {
				critical: this.riskLevelCount('critical'),
				high: this.riskLevelCount('high'),
				moderate: this.riskLevelCount('moderate'),
				low: this.riskLevelCount('low')
			},
			includedPriorityRisks: this.includedPriorityRisks(),
			workerConsultationLabel: this.workerConsultationLabel(),
			teamParticipationRows: this.teamParticipationRows(),
			actionSections: (this.vm.priorities || []).map((risk) => ({
				risk: risk,
				actions: this.actionsForRisk(risk)
			})),
			actionCount: includedActions.length,
			doneCount: includedActions.filter((action) => action.done).length
		};
	}

	getComplianceAssessmentAiRecommendations() {
		if (this.vm.complianceAiRecommendationsPending) return;
		const requestId = Date.now();
		this.vm.complianceAiRecommendationsRequestId = requestId;
		this.vm.complianceAiRecommendationsLoading = true;
		this.vm.complianceAiRecommendationsPending = true;
		this.vm.complianceAiRecommendationsDelayed = false;
		this.vm.complianceAiRecommendations = null;
		this.vm.complianceAiRecommendationsError = false;
		if (this.vm.complianceAiRecommendationsLoaderTimer) {
			clearTimeout(this.vm.complianceAiRecommendationsLoaderTimer);
		}
		this.vm.complianceAiRecommendationsLoaderTimer = setTimeout(() => {
			if (this.vm.complianceAiRecommendationsPending) {
				this.vm.complianceAiRecommendationsLoading = false;
				this.vm.complianceAiRecommendationsDelayed = true;
			}
		}, this.aiReportBlockingLoaderMs);
		if (this.vm.complianceAiRecommendationsTimeoutTimer) {
			clearTimeout(this.vm.complianceAiRecommendationsTimeoutTimer);
		}
		this.vm.complianceAiRecommendationsTimeoutTimer = setTimeout(() => {
			if (this.vm.complianceAiRecommendationsPending && this.vm.complianceAiRecommendationsRequestId === requestId) {
				this.handleComplianceAiRecommendationsError(new Error('Compliance AI report generation timed out.'));
			}
		}, this.aiReportMaxWaitMs);
		this.restService.getComplianceAssessmentAiRecommendations({
			questionnaireRequestId: this.vm.questionnaireRequestId,
			language: this.vm.user?.language || 'en',
			regenerate: true,
			reportType: 'FULL_PSR_REPORT',
			managementSummary: this.vm.reportNarrative || ''
			}).then((data: any) => {
				if (requestId !== this.vm.complianceAiRecommendationsRequestId || !this.vm.complianceAiRecommendationsPending) return;
				console.log('Compliance AI report response', data);
				this.clearComplianceAiLoadingState();
				this.vm.complianceAiRecommendations = this.formatComplianceAiResponse(data);
				if (!this.vm.complianceAiRecommendations) {
					this.handleComplianceAiRecommendationsError(new Error('Compliance AI report response did not include report content.'));
					return;
				}
			}).catch((err) => {
				if (requestId !== this.vm.complianceAiRecommendationsRequestId || !this.vm.complianceAiRecommendationsPending) return;
				this.handleComplianceAiRecommendationsError(err);
			});
		}

	formatComplianceAiResponse(data) {
		if (data === null || data === undefined) return '';
		if (typeof data === 'string') return data;
		return data.aiRecommendations || data.recommendations || data.summary || data.report || JSON.stringify(data, null, 2);
	}

	handleComplianceAiRecommendationsError(error) {
		this.clearComplianceAiLoadingState();
		this.vm.complianceAiRecommendationsError = true;
		console.error('Failed to generate compliance AI report', error);
	}

	clearComplianceAiLoadingState() {
		this.vm.complianceAiRecommendationsPending = false;
		this.vm.complianceAiRecommendationsLoading = false;
		this.vm.complianceAiRecommendationsDelayed = false;
		if (this.vm.complianceAiRecommendationsLoaderTimer) {
			clearTimeout(this.vm.complianceAiRecommendationsLoaderTimer);
			this.vm.complianceAiRecommendationsLoaderTimer = null;
		}
		if (this.vm.complianceAiRecommendationsTimeoutTimer) {
			clearTimeout(this.vm.complianceAiRecommendationsTimeoutTimer);
			this.vm.complianceAiRecommendationsTimeoutTimer = null;
		}
	}

	viewFullReport() {
		if (this.vm.resultsViewMode !== 'report') this.rememberProcessPage();
		if (!this.vm.complianceRecord) {
			this.vm.complianceRecord = this.buildComplianceRecordData();
		}
		this.vm.resultsViewMode = 'report';
		setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
	}

	generateFullAiReport() {
		this.saveReportNarrative();
		this.getComplianceAssessmentAiRecommendations();
	}

	aiReportSection(sectionId) {
		return '';
	}

	viewResults() {
		this.vm.resultsViewMode = 'results';
		setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
	}

	reportSummary() {
		var critical = (this.vm.risks || []).filter((risk) => this.riskClass(risk.riskLevel) === 'critical').length;
		var high = (this.vm.risks || []).filter((risk) => this.riskClass(risk.riskLevel) === 'high').length;
		var moderate = (this.vm.risks || []).filter((risk) => this.riskClass(risk.riskLevel) === 'moderate').length;
		var low = (this.vm.risks || []).filter((risk) => this.riskClass(risk.riskLevel) === 'low').length;
		return this.translate('Compliance_Assessment.Report_Summary_Template')
			.replace('%organisation%', this.vm.summary?.survey?.groupName || this.translate('Compliance_Assessment.The_Organisation'))
			.replace('%responded%', String(this.vm.respondedCount || 0))
			.replace('%sent%', String(this.vm.sentCount || 0))
			.replace('%rate%', String(this.vm.responseRate || 0))
			.replace('%riskCount%', String((this.vm.risks || []).length))
			.replace('%critical%', String(critical))
			.replace('%high%', String(high))
			.replace('%moderate%', String(moderate))
			.replace('%low%', String(low))
			.replace('%priorityCount%', String((this.vm.priorities || []).length))
			.replace('%controlCount%', String(this.includedActions().length));
	}

	reportCredibilitySentence() {
		var reportable = (this.vm.complianceRecord?.teamParticipationRows || this.teamParticipationRows()).filter((row) => !row.suppressed).length;
		var total = (this.vm.complianceRecord?.teamParticipationRows || this.teamParticipationRows()).length;
		var suppressed = Math.max(0, total - reportable);
		return (this.vm.respondedCount || 0) + ' of ' + (this.vm.sentCount || 0) + ' employees responded (' + (this.vm.responseRate || 0) + '%). '
			+ reportable + ' of ' + total + ' groups are reportable; ' + suppressed + ' fall below the reporting minimum and are suppressed. '
			+ ((this.vm.responseRate || 0) >= (this.vm.credibleResponseRate || 60)
				? 'The response rate meets the recommended credibility threshold.'
				: 'The response rate is below the recommended credibility threshold; interpret results with caution.');
	}

	teamParticipationInline() {
		return (this.vm.complianceRecord?.teamParticipationRows || this.teamParticipationRows())
			.map((row) => row.name + ': ' + (row.suppressed ? '<5' : row.responded))
			.join(' · ');
	}

	riskFinding(risk) {
		if (!risk) return '';
		var name = risk.nameKey ? this.translateOrFallback(risk.nameKey, this.riskDisplayName(risk)) : this.riskDisplayName(risk);
		if (risk.averageScore === null || risk.averageScore === undefined) {
			return name + ' is not reportable because there is insufficient data for anonymous group reporting.';
		}
		if (risk.exposure) {
			return 'Employees reported exposure in ' + name + ' at ' + this.scoreLabel(risk) + ' against a reference of ' + this.referenceLabel(risk) + '. Any exposure warrants a direct, confidential response.';
		}
		return 'The ' + name + ' result is ' + this.scoreLabel(risk) + ' against a reference of ' + this.referenceLabel(risk) + ', with a current status of ' + this.translate(this.riskLevelLabel(risk.riskLevel)).toLowerCase() + '.';
	}

	reportConclusion() {
		var actions = this.includedActions();
		var risks = this.includedPriorityRisks();
		var completed = actions.filter((action) => action.done).length;
		return 'The action plan assigns ' + actions.length + ' control' + (actions.length === 1 ? '' : 's')
			+ ' across ' + risks.length + ' risk area' + (risks.length === 1 ? '' : 's')
			+ ', of which ' + completed + ' ' + (completed === 1 ? 'is' : 'are') + ' completed. Progress is tracked by the named owners, with the next review on '
			+ this.formatReportDate(this.nextReviewDate()) + ' and the next annual assessment on ' + this.formatReportDate(this.nextAnnualAssessmentDate()) + '.';
	}

	formatReportDate(value) {
		var date = value ? new Date(value) : null;
		if (!date || isNaN(date.getTime())) return 'TBC';
		return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
	}

	loadReportNarrative() {
		return this.vm.complianceState?.reportNarrative || '';
	}

	saveReportNarrative() {
		return this.saveComplianceState();
	}

	scrollToComplianceRecord() {
		setTimeout(() => {
			var element = document.getElementById('complianceRecord');
			if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
		});
	}

	printPage() {
		window.print();
	}

	loadPlanCompleted() {
		return this.serverPlanCompleted();
	}

	savePlanCompleted(value) {
		this.vm.planCompleted = value && this.serverPlanCompleted();
		return this.saveComplianceState();
	}

	loadReviewerAssignments() {
		return { workerrep: {} };
	}

	saveReviewerAssignments() {
		this.vm.reviewerAssignments = this.vm.reviewerAssignments || { workerrep: {} };
		return this.saveComplianceState();
	}

	loadWorkerRepReview() {
		return { consulted: false, name: '' };
	}

	saveWorkerRepReview(consulted?: boolean) {
		this.vm.workerRep = this.vm.workerRep || { consulted: false, name: '' };
		if (typeof consulted === 'boolean') this.vm.workerRep.consulted = consulted;
		this.vm.readinessOverrides = this.vm.readinessOverrides || {};
		this.vm.readinessOverrides.workerrep = this.workerRepresentativeReviewed() ? 'complete' : 'incomplete';
		this.vm.readiness = this.buildReadiness(this.vm.summary?.calculatedResults?.readiness);
		this.updateReadinessCounts();
		return this.saveComplianceState();
	}

	complianceStatePayload() {
		return {
			questionnaireRequestId: this.vm.questionnaireRequestId,
			planCompleted: !!this.vm.planCompleted,
			readinessOverrides: this.vm.readinessOverrides || {},
			reviewerAssignments: this.vm.reviewerAssignments || { workerrep: {} },
			workerRep: this.vm.workerRep || { consulted: false, name: '' },
			reportNarrative: this.vm.reportNarrative || ''
		};
	}

	saveComplianceState() {
		var payload = this.complianceStatePayload();
		this.vm.complianceState = payload;
		return this.restService.saveComplianceAssessmentState({ questionnaireRequestId: this.vm.questionnaireRequestId }, payload).then((saved) => {
			this.vm.complianceState = saved || payload;
			return this.vm.complianceState;
		}).catch((err) => {
			console.warn('Could not save compliance assessment state', err);
			return payload;
		});
	}

	workerRepReadinessRow() {
		return (this.vm.readiness || []).find((row) => row.id === 'workerrep');
	}

	reviewerAccepted(role) {
		var assignment = (this.vm.reviewerAssignments || {})[role] || {};
		return !!assignment.acceptedAt;
	}

	serverPlanCompleted() {
		var actions = (this.vm.actions || []).filter((action) => !!action);
		return actions.length > 0 && actions.every((action) => action.owner && action.cutOffDate);
	}

	reviewActionPlanStorageKey() {
		return 'complianceAssessmentReviewActionPlan:' + this.vm.questionnaireRequestId;
	}

	consumeReviewActionPlanRequest() {
		try {
			var key = this.reviewActionPlanStorageKey();
			var requested = sessionStorage.getItem(key) === 'true';
			sessionStorage.removeItem(key);
			return requested;
		} catch (e) {
			return false;
		}
	}

	isReviewActionPlanRequest() {
		return this.route.snapshot.queryParamMap.get('step') === 'assign' || this.consumeReviewActionPlanRequest();
	}

	clearReviewActionPlanRequest() {
		try {
			sessionStorage.removeItem(this.reviewActionPlanStorageKey());
		} catch (e) {}
	}

	rememberProcessPage() {
		this.processHistory.push({
			actionPlanOnly: !!this.vm.actionPlanOnly,
			planStep: this.vm.planStep || 1,
			reviewingCompletedPlan: !!this.vm.reviewingCompletedPlan,
			resultsViewMode: this.vm.resultsViewMode || 'results',
			planCompleted: !!this.vm.planCompleted
		});
	}

	back() {
		const previous = this.processHistory.pop();
		if (previous) {
			const { planCompleted: wasCompleted, ...page } = previous;
			Object.assign(this.vm, page);
			// Keep the saved completion status while reopening the preceding editing step.
			if (previous.actionPlanOnly && this.vm.planCompleted && !wasCompleted) {
				this.vm.reviewingCompletedPlan = true;
			}
			this.clearReviewActionPlanRequest();
			this.location.replaceState('/compliance/' + this.vm.questionnaireRequestId + (previous.actionPlanOnly ? '/action-plan' : '/results'));
		} else if (!this.vm.actionPlanOnly && this.vm.resultsViewMode === 'report') {
			this.viewComplianceRecord(false);
		} else if (!this.vm.actionPlanOnly && this.vm.resultsViewMode === 'record') {
			this.viewResults();
		} else if (this.vm.actionPlanOnly && this.vm.planCompleted && !this.vm.reviewingCompletedPlan) {
			this.vm.reviewingCompletedPlan = true;
			this.vm.planStep = 2;
		} else if (this.vm.actionPlanOnly && this.vm.planStep === 2) {
			this.goToPriorities();
		} else if (this.vm.actionPlanOnly) {
			this.returnToDashboard();
		} else {
			this.location.back();
		}
	}

	returnToDashboard() {
		this.clearReviewActionPlanRequest();
		this.angularRoutingHelperService.setPath('/compliance/' + this.vm.questionnaireRequestId + '/results');
	}

	isPriority(level) {
		var value = (level || '').toString().toLowerCase();
		return value === 'moderate' || value === 'medium' || value === '2'
			|| value === 'high' || value === 'critical' || value === '3' || value === '4';
	}

	riskWeight(level) {
		var value = (level || '').toString().toLowerCase();
		if (value === 'critical' || value === '4') return 4;
		if (value === 'high' || value === '3') return 3;
		if (value === 'moderate' || value === 'medium' || value === '2') return 2;
		if (value === 'low' || value === '1') return 1;
		return 0;
	}

	riskClass(level) {
		var value = (level || '').toString().toLowerCase();
		if (value === '4') return 'critical';
		if (value === '3') return 'high';
		if (value === '2' || value === 'medium') return 'moderate';
		if (value === '1') return 'low';
		return value || 'na';
	}

	riskLevelLabel(level) {
		var value = this.riskClass(level);
		if (value === 'na') return 'Compliance_Assessment.Risk_Not_Scored';
		return 'Compliance_Assessment.Risk_' + value.charAt(0).toUpperCase() + value.slice(1);
	}

	openRisk(risk) {
		this.vm.selectedRisk = risk;
	}

	closeRisk() {
		this.vm.selectedRisk = null;
	}

	recommendedControls(risk) {
		if (!risk) return [];
		var controls = (this.controlTemplates || []).filter((control) => control.dimensionId === risk.id);
		if (controls.length) return controls.slice(0, 4);
		return [{
			title: this.translate('Compliance_Assessment.Fallback_Control_Title').replace('%risk%', risk.name || this.translate('Compliance_Assessment.Risk_Area')),
			description: this.translate('Compliance_Assessment.Fallback_Control_Description')
		}];
	}

	shouldShowPulse(risk) {
		return !!risk && this.isPriority(risk.riskLevel);
	}

	pulseQuestionCount(risk) {
		if (!risk) return 0;
		if (risk.exposure || risk.id === 'offensive') return 4;
		return (this.itemDimensions || []).filter((item) => item.dim === risk.id).length;
	}

	launchPulseSurvey(risk) {
		if (!risk) return;
		this.closeRisk();
		this.angularRoutingHelperService.setPath('/create-employee-surveys/COMPLIANCE_ASSESSMENT', null, false, {
			pulse: 'true',
			pulseRiskId: risk.id || '',
			pulseRiskName: this.riskDisplayName(risk),
			sourceQuestionnaireRequestId: this.vm.questionnaireRequestId
		});
	}

	readinessClass(state) {
		if (state === 'complete') return 'low';
		if (state === 'na') return 'na';
		return 'critical';
	}

	scoreWidth(risk) {
		if (!risk || risk.averageScore === null || risk.averageScore === undefined) return 0;
		return Math.max(0, Math.min(100, risk.exposure ? Number(risk.averageScore) * 4 : Number(risk.averageScore)));
	}

	benchmarkLeft(risk) {
		if (!risk) return 0;
		return Math.max(0, Math.min(100, risk.exposure ? Number(risk.benchmark) * 4 : Number(risk.benchmark)));
	}

	scoreLabel(risk) {
		if (!risk || risk.averageScore === null || risk.averageScore === undefined) {
			return 'Compliance_Assessment.Not_Reportable';
		}
		var rounded = Math.round(Number(risk.averageScore));
		return risk.exposure ? rounded + '% ' + this.translate('Compliance_Assessment.Exposed') : String(rounded);
	}

	referenceLabel(risk) {
		if (!risk) return '';
		var referenceMean = risk.referenceMean !== null && risk.referenceMean !== undefined ? risk.referenceMean : risk.benchmark;
		if (referenceMean === null || referenceMean === undefined) return '';
		if (risk.exposure) return this.translate('Compliance_Assessment.Reference_Abbrev') + ' ' + Math.round(Number(referenceMean)) + '%';
		var sd = risk.referenceSd !== null && risk.referenceSd !== undefined ? Number(risk.referenceSd) : null;
		var label = this.translate('Compliance_Assessment.Reference_Mean') + ' ' + Math.round(Number(referenceMean));
		if (sd) label += ', SD ' + Math.round(sd);
		if (risk.zScore !== null && risk.zScore !== undefined) label += ', z ' + Number(risk.zScore).toFixed(1);
		return label;
	}

	employeeName(employee) {
		if (!employee) return '';
		return [employee.firstName || employee.firstname, employee.lastName || employee.lastname].filter(Boolean).join(' ') || employee.email || '';
	}

	dedupeActions(actions) {
		var byKey = {};
		(actions || []).forEach((action) => {
			if (!action) return;
			var key = this.actionLogicalKey(action);
			var existing = byKey[key];
			if (!existing || this.actionCompletenessScore(action) >= this.actionCompletenessScore(existing)) {
				byKey[key] = action;
			}
		});
		return Object.keys(byKey).map((key) => byKey[key]);
	}

	actionLogicalKey(action) {
		if (!action) return '';
		if (action.controlKey && String(action.controlKey).indexOf('custom::') !== -1) return action.controlKey;
		var dimensionId = action.dimensionId || this.inferDimensionId(action.riskArea, action) || this.normalizeText(action.riskArea);
		var template = action.titleKey || this.findControlTemplateByTitle(action.title)?.titleKey || this.normalizeText(action.title);
		return dimensionId + '::' + template;
	}

	actionCompletenessScore(action) {
		if (!action) return 0;
		return (action.id ? 16 : 0)
			+ (action.owner ? 8 : 0)
			+ (action.ownerToken ? 4 : 0)
			+ (action.ownerInput ? 2 : 0)
			+ (action.ownerSubmittedAt ? 1 : 0);
	}

	employeeId(employee) {
		if (!employee) return null;
		return employee.worker?.employee?.id || employee.employee?.id || employee.user?.id || employee.employeeUserId || employee.userId || employee.accountUserId || employee.id || null;
	}

	userFromEmployee(employee) {
		if (!employee) return null;
		var user = employee.worker?.employee || employee.employee || employee.user || employee;
		return {
			id: this.employeeId(employee),
			firstname: user.firstName || user.firstname || employee.firstName || employee.firstname,
			lastname: user.lastName || user.lastname || employee.lastName || employee.lastname,
			email: user.email || employee.email,
			role: user.role || user.position || user.jobTitle || user.title || employee.role || employee.position || employee.jobTitle || employee.title
		};
	}

	normalizeActionUser(action, field) {
		var user = action ? action[field] : null;
		var userId = this.employeeId(user) || action?.[field + 'UserId'] || action?.[field + '_user_id'] || action?.[field + 'Id'];
		var matched = this.findEmployeeById(userId);
		if (matched) return this.userFromEmployee(matched);
		if (user) return this.userFromEmployee(user);
		if (userId) return { id: userId };
		return null;
	}

	normalizeUserForSave(user) {
		if (!user) return null;
		return {
			id: this.employeeId(user),
			firstname: user.firstname || user.firstName,
			lastname: user.lastname || user.lastName,
			email: user.email,
			role: user.role || user.position || user.jobTitle || user.title
		};
	}

	findEmployeeById(userId) {
		if (userId === null || userId === undefined) return null;
		var normalizedId = String(userId);
		return (this.vm.employees || []).find((employee) => {
			return String(this.employeeId(employee)) === normalizedId
				|| String(employee.id) === normalizedId
				|| String(employee.worker?.id) === normalizedId;
		}) || null;
	}

	employeeRole(employee) {
		if (!employee) return '';
		return employee.role || employee.position || employee.jobTitle || employee.title || '';
	}

	riskDisplayName(risk) {
		return (risk && (risk.name || risk.riskArea || risk.title)) || this.dimensionFallbackName(risk?.id);
	}

	sameRiskArea(a, b) {
		return (a || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '') === (b || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
	}

	defaultSuccessIndicator(riskArea) {
		return this.translate('Compliance_Assessment.Default_Success_Indicator').replace('%risk%', riskArea || this.translate('Compliance_Assessment.Risk'));
	}

	withSuccessIndicator(description, successIndicator) {
		var text = description || '';
		if (!successIndicator) return text;
		var marker = '\n\nSuccess indicator: ';
		var base = text.split(marker)[0];
		return base + marker + successIndicator;
	}

	questionBelongsToDimension(question, dimensionId) {
		var normalizedQuestion = this.normalizeText(question);
		return this.itemDimensions.some((item) => item.dim === dimensionId && normalizedQuestion.indexOf(this.normalizeText(this.itemDimensionText(item))) !== -1);
	}

	itemDimensionText(item: any) {
		return item && item.textKey ? this.translate(item.textKey) : (item && item.text) || '';
	}

	calculateDimensionRiskLevel(score, dimension) {
		if (dimension.exposure) {
			if (score > dimension.benchmark + 15) return 'critical';
			if (score > dimension.benchmark + 5) return 'high';
			if (score > dimension.benchmark) return 'moderate';
			return 'low';
		}
		if (!dimension.referenceSd) return 'NA';
		var zScore = (score - dimension.benchmark) / dimension.referenceSd;
		if (zScore < -2.0) return 'critical';
		if (zScore < -1.0) return 'high';
		if (zScore < -0.5) return 'moderate';
		return 'low';
	}

	inferDimensionId(riskArea, action?) {
		var normalized = this.normalizeText(riskArea);
		var riskAreaKey = action?.riskAreaKey || this.findControlTemplateByTitle(action?.title)?.riskAreaKey;
		if (riskAreaKey) {
			var keyedDimension = this.dimensions.find((item) => item.nameKey === riskAreaKey);
			if (keyedDimension) return keyedDimension.id;
		}
		var dimension = this.dimensions.find((item) => normalized === this.normalizeText(this.dimensionFallbackName(item.id))
			|| normalized === this.normalizeText(this.translateOrFallback(item.nameKey, this.dimensionFallbackName(item.id))));
		return dimension ? dimension.id : null;
	}

	dimensionFallbackName(id) {
		var names = {
			demands: 'Workload & work pace',
			emo: 'Emotional demands',
			infl: 'Influence at work',
			dev: 'Development possibilities',
			mean: 'Meaning of work',
			recog: 'Recognition & reward',
			role: 'Role clarity',
			lead: 'Quality of leadership',
			support: 'Social support & community',
			security: 'Job security & fairness',
			worklife: 'Work-life balance',
			connect: 'Digital connectivity outside hours',
			algo: 'Algorithmic monitoring & decisions',
			offensive: 'Offensive behaviour'
		};
		return names[id] || id;
	}

	normalizeText(value) {
		return (value || '').toString().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
	}

	successIndicatorFromDescription(description) {
		var marker = '\n\nSuccess indicator: ';
		var text = description || '';
		if (text.indexOf(marker) === -1) return '';
		return text.split(marker)[1] || '';
	}

	dateInputValue(value) {
		if (!value) return null;
		if (typeof value === 'string' && value.length >= 10) return value.substring(0, 10);
		var date = new Date(value);
		if (isNaN(date.getTime())) return null;
		return date.toISOString().substring(0, 10);
	}

	defaultActionDueDate() {
		var date = new Date(this.assessmentDate());
		if (isNaN(date.getTime())) date = new Date();
		date.setDate(date.getDate() + this.defaultActionDueDays);
		return date.toISOString().substring(0, 10);
	}

	toServerDate(value) {
		if (!value) return null;
		if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
			return value + 'T00:00:00.000Z';
		}
		return value;
	}
}
